import { type StaffShop, unwrap } from '@iris/shared';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { selectedMerchant } from './api';

const STORAGE_KEY = 'iris.merchantId';

interface ShopContextValue {
  shops: UseQueryResult<StaffShop[]>;
  /** the shop every merchant request is about (`X-Merchant-Id`), or null before the first shop exists */
  current: StaffShop | null;
  select(merchantId: string | null): void;
  /** A shop just created: added to the list and selected at once, so no request goes to the previous shop. */
  adopt(shop: StaffShop): void;
}

const ShopContext = createContext<ShopContextValue | null>(null);

function readSaved(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The shops the user works in (`GET /me/staff`) and the selected one (ADR 0001 D8, Q-57). The choice is kept on
 * the device; with a single shop it is simply that one. The API client sends it as `X-Merchant-Id`.
 */
/**
 * A shop staff can work in: ACTIVE, or SUSPENDED by Ops — the counter stays open there so issued vouchers are still
 * validated (Q-39, ADR 0007 D2); the server refuses earns (MERCHANT_INACTIVE).
 */
export const isOperating = (status: string | undefined) => status === 'ACTIVE' || status === 'SUSPENDED';

export function ShopProvider({ children }: { children: ReactNode }) {
  const { api, status, user } = useAuth();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(readSaved);
  const shops = useQuery<StaffShop[]>({
    queryKey: ['myShops', user?.id],
    queryFn: async () => unwrap(await api.GET('/api/v1/me/staff')),
    enabled: status === 'authenticated',
    staleTime: 30_000,
  });

  const list = shops.data ?? [];
  const current = list.find((shop) => shop.merchantId === chosen) ?? list[0] ?? null;
  // set synchronously so the very next request already carries the right shop
  selectedMerchant.id = current?.merchantId ?? null;

  const select = useCallback((merchantId: string | null) => {
    selectedMerchant.id = merchantId;
    setChosen(merchantId);
    try {
      if (merchantId) window.localStorage.setItem(STORAGE_KEY, merchantId);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // private mode: the choice lasts for this visit
    }
  }, []);

  const adopt = useCallback(
    (shop: StaffShop) => {
      queryClient.setQueryData<StaffShop[]>(['myShops', user?.id], (old = []) => [
        ...old.filter((s) => s.merchantId !== shop.merchantId),
        shop,
      ]);
      select(shop.merchantId);
    },
    [queryClient, select, user?.id],
  );

  const value = useMemo(() => ({ shops, current, select, adopt }), [shops, current, select, adopt]);
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShops(): ShopContextValue {
  const value = useContext(ShopContext);
  if (!value) throw new Error('useShops must be used inside <ShopProvider>');
  return value;
}
